<template>
  <div class="bg-white shadow-md rounded-xl p-6 h-full">
    <h2 class="text-xl font-semibold text-gray-800">User Management</h2>
    <p class="text-sm text-gray-500 mt-1 border-b pb-4">Create and manage standard users for your client account.</p>
    <div class="grid grid-cols-1 md:grid-cols-2 gap-8 mt-4">
      <div>
        <h3 class="font-medium text-gray-900">Add New Standard User</h3>
        <form @submit.prevent="handleAddUser" class="mt-4 space-y-4">
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label for="newUserFirstName" class="block text-sm font-medium text-gray-700">First Name</label><input v-model="form.newUserFirstName" type="text" id="newUserFirstName" required class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-brand-purple focus:border-brand-purple"></div>
            <div><label for="newUserLastName" class="block text-sm font-medium text-gray-700">Last Name</label><input v-model="form.newUserLastName" type="text" id="newUserLastName" required class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-brand-purple focus:border-brand-purple"></div>
          </div>
          <div><label for="newUserEmail" class="block text-sm font-medium text-gray-700">User Email</label><input v-model="form.newUserEmail" type="email" id="newUserEmail" required class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-brand-purple focus:border-brand-purple"></div>
          <button type="submit" :disabled="isProcessing" class="w-full bg-brand-purple-dark text-white py-2 px-4 rounded-md hover:opacity-90 disabled:opacity-50"><span v-if="isProcessing">Adding User...</span><span v-else>Add User</span></button>
        </form>
        <div v-if="feedbackMessage" class="mt-4 p-3 rounded-md text-sm" :class="isError ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'"><p v-html="feedbackMessage"></p></div>
      </div>
      <div>
        <h3 class="font-medium text-gray-900">Existing Users</h3>
        <div class="mt-4">
           <div v-if="loading" class="space-y-3 animate-pulse"><div v-for="i in 3" :key="i" class="p-3 bg-gray-100 rounded-md flex items-center h-16"></div></div>
           <div v-else-if="users && users.length === 0" class="text-center border-2 border-dashed border-gray-200 rounded-lg p-8 h-full flex flex-col justify-center">
              <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path vector-effect="non-scaling-stroke" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M15 21a6 6 0 00-9-5.197m0 0A4 4 0 119.646 5.243M15 21a6 6 0 00-9-5.197" /></svg>
              <h3 class="mt-4 text-lg font-semibold text-gray-800">Add your first user</h3><p class="mt-1 text-sm text-gray-500">Use the form on the left to create a standard user account for your team.</p>
           </div>
           <ul v-else-if="users" class="space-y-3">
            <li v-for="user in users" :key="user.id" class="p-3 bg-gray-50 rounded-md flex items-center">
              <img v-if="user.photoURL" :src="user.photoURL" class="h-10 w-10 rounded-full object-cover mr-4">
              <div v-else class="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center font-bold text-gray-500 mr-4 flex-shrink-0">{{ user.firstName ? user.firstName.charAt(0).toUpperCase() : '' }}</div>
              <div class="flex-grow"><p class="font-semibold text-gray-700">{{ user.firstName }} {{ user.lastName }}</p><p class="text-sm text-gray-500">{{ user.email }}</p></div>
              <div class="flex-shrink-0"><button @click="promptEditUser(user)" class="text-sm font-medium text-brand-purple-dark hover:underline">Edit</button><button @click="promptRemoveUser(user)" class="ml-4 text-sm font-medium text-red-600 hover:underline">Remove</button></div>
            </li>
          </ul>
        </div>
      </div>
    </div>
    <div v-if="showRemoveModal" class="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4"><div class="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full"><h3 class="text-lg font-bold text-gray-900">Confirm Removal</h3><p class="mt-2 text-sm text-gray-600">Are you sure you want to remove the user <span class="font-semibold">{{ userToRemove.firstName }} {{ userToRemove.lastName }}</span>? This action cannot be undone.</p><div class="mt-6 flex justify-end space-x-3"><button @click="cancelRemoveUser" class="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">Cancel</button><button @click="handleRemoveUser" :disabled="isProcessing" class="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"><span v-if="isProcessing">Removing...</span><span v-else>Remove User</span></button></div></div></div>
    <div v-if="showEditModal" class="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4"><div class="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full"><h3 class="text-lg font-bold text-gray-900">Edit User</h3><p class="mt-2 text-sm text-gray-600">Update the details for {{ userToEdit.email }}.</p><form @submit.prevent="handleUpdateUser" class="mt-4 space-y-4"><div class="grid grid-cols-1 sm:grid-cols-2 gap-4"><div><label for="editUserFirstName" class="block text-sm font-medium text-gray-700">First Name</label><input v-model="editForm.firstName" type="text" id="editUserFirstName" required class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-brand-purple focus:border-brand-purple"></div><div><label for="editUserLastName" class="block text-sm font-medium text-gray-700">Last Name</label><input v-model="editForm.lastName" type="text" id="editUserLastName" required class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-brand-purple focus:border-brand-purple"></div></div><div class="mt-6 flex justify-end space-x-3"><button @click="cancelEditUser" type="button" class="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">Cancel</button><button type="submit" :disabled="isProcessing" class="px-4 py-2 bg-brand-purple-dark text-white rounded-md hover:opacity-90 disabled:opacity-50"><span v-if="isProcessing">Saving...</span><span v-else>Save Changes</span></button></div></form></div></div>
  </div>
</template>

<script>
import { getFunctions, httpsCallable } from 'firebase/functions';

export default {
  name: 'UserManagementPanel',
  props: {
    users: {
      type: Array,
      required: true
    },
    loading: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      form: { newUserFirstName: '', newUserLastName: '', newUserEmail: '' },
      isProcessing: false, feedbackMessage: '', isError: false,
      showRemoveModal: false, userToRemove: null,
      showEditModal: false, userToEdit: null,
      editForm: { firstName: '', lastName: '' },
    }
  },
  methods: {
    getFunctionsInstance() {
      return getFunctions(undefined, 'europe-west2');
    },
    async handleAddUser() {
      this.isProcessing = true;
      this.feedbackMessage = '';
      this.isError = false;
      try {
        const functions = this.getFunctionsInstance();
        const createStandardUser = httpsCallable(functions, 'createStandardUser');
        const response = await createStandardUser(this.form);

        if (response.data.success) {
          this.feedbackMessage = response.data.message;
          this.resetForm();
          this.$emit('users-changed');
        } else {
          throw new Error("The server returned an invalid response.");
        }
      } catch (error) {
        console.error("Error creating standard user:", error);
        this.feedbackMessage = error.message;
        this.isError = true;
      } finally {
        this.isProcessing = false;
      }
    },
    resetForm() {
      this.form = { newUserFirstName: '', newUserLastName: '', newUserEmail: '' };
    },
    promptRemoveUser(user) {
      this.userToRemove = user; this.showRemoveModal = true; this.feedbackMessage = ''; this.isError = false;
    },
    cancelRemoveUser() {
      this.showRemoveModal = false; this.userToRemove = null;
    },
    async handleRemoveUser() {
      this.isProcessing = true;
      try {
        const functions = this.getFunctionsInstance();
        const deleteStandardUser = httpsCallable(functions, 'deleteStandardUser');
        await deleteStandardUser({ userId: this.userToRemove.id });
        this.feedbackMessage = `Successfully removed user ${this.userToRemove.email}.`;
        this.$emit('users-changed');
      } catch (error) {
        console.error("Error removing user:", error);
        this.feedbackMessage = error.message; this.isError = true;
      } finally {
        this.isProcessing = false; this.cancelRemoveUser();
      }
    },
    promptEditUser(user) {
      this.userToEdit = user;
      this.editForm = { firstName: user.firstName, lastName: user.lastName };
      this.showEditModal = true;
      this.feedbackMessage = '';
      this.isError = false;
    },
    cancelEditUser() {
      this.showEditModal = false; this.userToEdit = null; this.editForm = { firstName: '', lastName: '' };
    },
    async handleUpdateUser() {
      this.isProcessing = true;
      try {
        const functions = this.getFunctionsInstance();
        const updateStandardUser = httpsCallable(functions, 'updateStandardUser');
        const payload = { userId: this.userToEdit.id, firstName: this.editForm.firstName, lastName: this.editForm.lastName };
        await updateStandardUser(payload);
        this.feedbackMessage = `Successfully updated user ${this.userToEdit.email}.`;
        this.$emit('users-changed');
      } catch (error) {
        console.error("Error updating user:", error);
        this.feedbackMessage = error.message; this.isError = true;
      } finally {
        this.isProcessing = false; this.cancelEditUser();
      }
    },
  },
};
</script>